/*
 * PWC Lab — storefront behaviour layer.
 * Plain script (no build step). Everything hangs off data-pw-* attributes so
 * sections can be added/removed in the theme editor without touching this file.
 */
(() => {
  'use strict';

  const PWC = (window.PWC = window.PWC || {});
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const store = {
    get(key, fallback) {
      try {
        const v = localStorage.getItem(key);
        return v ? JSON.parse(v) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable — feature degrades gracefully */
      }
    },
  };
  PWC.store = store;

  /* ------------------------------------------------------------------ money */
  PWC.money = (cents) => {
    const fmt = PWC.moneyFormat || '€{{amount}}';
    const n = Number(cents || 0) / 100;
    const two = n.toFixed(2);
    const withCommas = (s, sep = ',', dec = '.') => {
      const [a, b] = s.split('.');
      return a.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (b ? dec + b : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals':
          return withCommas(Math.round(n).toString());
        case 'amount_with_comma_separator':
          return withCommas(two, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(Math.round(n).toString(), '.');
        default:
          return withCommas(two);
      }
    });
  };

  const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  PWC.escape = escapeHtml;

  /* ------------------------------------------------------------------- cart */
  PWC.getCart = () => fetch(`${PWC.routes.root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  PWC.addItems = async (items, opts = {}) => {
    const sections = cartSectionIds();
    const res = await fetch(PWC.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(sections.length ? { items, sections: sections.join(','), sections_url: location.pathname } : { items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || 'Could not add to cart');
      err.data = data;
      throw err;
    }
    const cart = await PWC.getCart();
    PWC.syncCart(cart, opts.sourceId || 'pwc', data.sections);
    if (!opts.silent) PWC.toastAdded(data.items || [], cart);
    return { added: data.items || [], cart };
  };

  PWC.syncCart = (cart, sourceId = 'pwc', sections) => {
    PWC.cartCount = cart.item_count;
    PWC.cartTotal = cart.total_price;
    // Horizon's cart icon + drawer listen for `cart:update` (ThemeEvents.cartUpdate).
    const evt = new Event('cart:update', { bubbles: true });
    evt.detail = { resource: cart, sourceId, data: { source: 'pwc', itemCount: cart.item_count, ...(sections ? { sections } : {}) } };
    document.dispatchEvent(evt);
    $$('[data-pw-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    PWC.renderShipMeters();
  };

  /* Free-shipping meter (toast, product page, cart drawer) */
  PWC.shipMeterHTML = (total) => {
    const goal = PWC.freeShipping || 0;
    if (!goal) return '';
    if (!total) return '';
    const pct = Math.min(100, Math.round((total / goal) * 100));
    const left = Math.max(0, goal - total);
    const msg =
      left > 0
        ? `<strong>${PWC.money(left)}</strong> to go for ${escapeHtml(PWC.freeShippingLabel)}`
        : `<strong>You’ve unlocked ${escapeHtml(PWC.freeShippingLabel)}!</strong>`;
    return `<div class="pw-meter${left <= 0 ? ' is-done' : ''}"><p>${msg}</p><div class="pw-meter__bar"><span style="width:${pct}%"></span></div></div>`;
  };
  PWC.renderShipMeters = () => {
    $$('[data-pw-shipmeter]').forEach((el) => {
      el.innerHTML = PWC.shipMeterHTML(PWC.cartTotal || 0);
    });
    const drawer = $('.cart-drawer__inner');
    if (drawer && PWC.freeShipping) {
      let m = $('.pw-drawer-meter', drawer);
      if (!m) {
        m = document.createElement('div');
        m.className = 'pw-drawer-meter pw-scope';
        const head = $('.cart-drawer__header', drawer);
        head ? head.after(m) : drawer.prepend(m);
      }
      m.innerHTML = PWC.shipMeterHTML(PWC.cartTotal || 0);
    }
  };

  /* Toast */
  let toastTimer;
  PWC.toast = (html, { timeout = 4200, cls = '' } = {}) => {
    let el = $('#pw-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pw-toast';
      el.className = 'pw-toast pw-scope';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-pw-toast-close]')) PWC.hideToast();
        if (e.target.closest('[data-pw-open-cart]')) {
          e.preventDefault();
          PWC.hideToast();
          PWC.openCart();
        }
      });
      el.addEventListener('pointerenter', () => clearTimeout(toastTimer));
      el.addEventListener('pointerleave', () => (toastTimer = setTimeout(PWC.hideToast, 2000)));
    }
    el.className = `pw-toast pw-scope ${cls}`;
    el.innerHTML = html;
    requestAnimationFrame(() => el.classList.add('is-on'));
    clearTimeout(toastTimer);
    if (timeout) toastTimer = setTimeout(PWC.hideToast, timeout);
  };
  PWC.hideToast = () => $('#pw-toast')?.classList.remove('is-on');

  PWC.openCart = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      setTimeout(PWC.renderShipMeters, 60);
    } else {
      window.location.href = PWC.routes.cart;
    }
  };

  // Compact "added" note. The free-shipping line only shows once it's close (or just reached),
  // so a €1 card doesn't get "€249 away from free shipping" in its face every time.
  let lastShipNote = 0;
  PWC.toastAdded = (items, cart) => {
    const first = items[0];
    const count = items.reduce((n, i) => n + (i.quantity || 1), 0);
    const img = first?.image ? `<img src="${first.image}${first.image.includes('?') ? '&' : '?'}width=120" alt="">` : '';
    const title =
      items.length > 1 ? `${count} cards added` : `${escapeHtml((first?.product_title || first?.title || 'Card').split(' - ')[0])} added`;
    const goal = PWC.freeShipping || 0;
    const total = cart.total_price || 0;
    const before = total - items.reduce((n, i) => n + (i.final_line_price || i.line_price || 0), 0);
    const justUnlocked = goal && total >= goal && before < goal;
    const close = goal && total < goal && total >= goal * 0.6;
    let ship = '';
    if (justUnlocked) ship = `<span class="pw-toast__ship is-done">Free shipping unlocked!</span>`;
    else if (close && Date.now() - lastShipNote > 60000) {
      lastShipNote = Date.now();
      ship = `<span class="pw-toast__ship">${PWC.money(goal - total)} to go for free shipping</span>`;
    }
    PWC.toast(
      `<div class="pw-toast__main">
        <div class="pw-toast__img">${img}<span class="pw-toast__tick" aria-hidden="true"><svg viewBox="0 0 20 20"><path d="M4 10.5l4 4 8-9"/></svg></span></div>
        <div class="pw-toast__text"><strong>${title}</strong><span class="pw-muted">${cart.item_count} in your cart · ${PWC.money(total)}</span>${ship}</div>
        <a class="pw-btn pw-btn--sm" href="${PWC.routes.cart}" data-pw-open-cart>Cart</a>
      </div>`,
      { timeout: 2600, cls: 'pw-toast--compact' }
    );
  };

  /* Quick-add forms: product cards, rows and the product page */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-pw-add]');
    if (!form) return;
    if (e.submitter && e.submitter.name === 'return_to') return; // "Buy it now" → normal POST → checkout
    e.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Math.max(1, Number(fd.get('quantity') || 1));
    if (!id) return;
    const btn = e.submitter || form.querySelector('[type="submit"]');
    btn?.classList.add('is-busy');
    btn?.setAttribute('aria-busy', 'true');
    try {
      await PWC.addItems([{ id, quantity }], { sourceId: String(id) });
      flyToCart(form.closest('.pw-card, .pw-row, [data-pw-pdp]'));
      btn?.classList.add('is-done');
      setTimeout(() => btn?.classList.remove('is-done'), 1800);
    } catch (err) {
      PWC.toast(
        `<div class="pw-toast__main"><div class="pw-toast__text"><span class="pw-eyebrow">Couldn’t add</span><strong>${escapeHtml(
          err.message
        )}</strong></div><button type="button" class="pw-toast__x" data-pw-toast-close aria-label="Dismiss"><svg viewBox="0 0 20 20"><path d="M5 5l10 10M15 5L5 15"/></svg></button></div>`,
        { cls: 'is-error' }
      );
    } finally {
      btn?.classList.remove('is-busy');
      btn?.removeAttribute('aria-busy');
    }
  });

  /* Card image flies to the cart icon */
  function flyToCart(source) {
    if (reduceMotion || !source) return;
    const img = source.querySelector('img');
    const target =
      $('[data-pw-cart-target]:not([hidden])') ||
      $('cart-icon') ||
      $('.header-actions__cart-icon') ||
      $('[data-pw-bottomnav] [data-pw-cart-target]');
    if (!img || !target) return;
    const a = img.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    if (!a.width || !b.width) return;
    const ghost = img.cloneNode();
    ghost.removeAttribute('srcset');
    ghost.src = img.currentSrc || img.src;
    ghost.className = 'pw-fly';
    Object.assign(ghost.style, { left: `${a.left}px`, top: `${a.top}px`, width: `${a.width}px`, height: `${a.height}px` });
    document.body.appendChild(ghost);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    ghost
      .animate(
        [
          { transform: 'translate(0,0) scale(1) rotate(0)', opacity: 1 },
          { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - 80}px) scale(.55) rotate(-8deg)`, opacity: 0.95, offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(.08) rotate(12deg)`, opacity: 0.2 },
        ],
        { duration: 720, easing: 'cubic-bezier(.5,0,.2,1)' }
      )
      .finished.then(() => {
        ghost.remove();
        target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 380 });
      });
  }

  /* ------------------------------------------------------------ tilt + holo */
  function initTilt(root = document) {
    if (!finePointer || reduceMotion) return;
    $$('[data-pw-tilt]', root).forEach((el) => {
      if (el.__pwTilt) return;
      el.__pwTilt = true;
      const strong = el.hasAttribute('data-pw-tilt-strong');
      const max = strong ? 14 : 10;
      let raf = 0;
      const host = el.closest('.pw-card, .pw-stage, .pw-fan__card') || el;
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.setProperty('--rx', `${(0.5 - py) * max}deg`);
          el.style.setProperty('--ry', `${(px - 0.5) * max}deg`);
          host.style.setProperty('--mx', `${px * 100}%`);
          host.style.setProperty('--my', `${py * 100}%`);
          host.style.setProperty('--hyp', Math.min(1, Math.hypot(px - 0.5, py - 0.5) * 2).toFixed(3));
          host.classList.add('is-tilting');
        });
      };
      const onLeave = () => {
        cancelAnimationFrame(raf);
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
        host.classList.remove('is-tilting');
      };
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
    });
  }

  /* ------------------------------------------------------- reveal / counts */
  const revealIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting || en.boundingClientRect.top < 0) {
                en.target.classList.add('is-in');
                revealIO.unobserve(en.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;
  function initReveal(root = document) {
    $$('[data-pw-reveal]:not(.is-in)', root).forEach((el) => (revealIO && !reduceMotion ? revealIO.observe(el) : el.classList.add('is-in')));
  }

  function initCounts() {
    $$('[data-pw-count]').forEach((el) => {
      const target = Number(el.dataset.pwCount || 0);
      if (reduceMotion || !target) return;
      const io = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const dur = 1200;
        const tick = (t) => {
          const k = Math.min(1, (t - t0) / dur);
          const eased = 1 - Math.pow(1 - k, 4);
          el.textContent = Math.round(target * eased).toLocaleString('en-IE');
          if (k < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ rails */
  const railIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting || en.boundingClientRect.top < 0) {
                railIO.unobserve(en.target);
                loadRail(en.target);
              }
            });
          },
          { rootMargin: '700px 0px' }
        )
      : null;

  async function loadRail(track) {
    const url = track.dataset.pwRail;
    try {
      const html = await fetch(url, { credentials: 'same-origin' }).then((r) => r.text());
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const exclude = track.dataset.exclude;
      if (exclude) $$(`[data-product-id="${exclude}"]`, tmp).forEach((n) => n.remove());
      const cards = $$('.pw-card', tmp);
      if (!cards.length) {
        track.closest('section')?.setAttribute('hidden', '');
        return;
      }
      track.innerHTML = '';
      cards.forEach((c, i) => {
        c.style.setProperty('--d', `${Math.min(i, 8) * 45}ms`);
        c.classList.add('pw-pop');
        track.appendChild(c);
      });
      const link = track.dataset.pwRailLink;
      if (link) {
        const more = document.createElement('a');
        more.href = link;
        more.className = 'pw-railmore';
        more.innerHTML = '<span>See all</span><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5"/></svg>';
        track.appendChild(more);
      }
      hydrate(track);
    } catch (e) {
      track.closest('section')?.setAttribute('hidden', '');
    }
  }

  function initRails(root = document) {
    $$('[data-pw-rail]', root).forEach((t) => (railIO ? railIO.observe(t) : loadRail(t)));
    $$('[data-pw-rail-prev], [data-pw-rail-next]', root).forEach((btn) => {
      if (btn.__pw) return;
      btn.__pw = true;
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-pw-railwrap]') || btn.closest('section');
        const track = wrap?.querySelector('[data-pw-track], .pw-rail__track');
        if (!track) return;
        const dir = btn.hasAttribute('data-pw-rail-next') ? 1 : -1;
        track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------------------------------------------------------- instant search */
  function initInstant(root = document) {
    $$('form[data-pw-instant]', root).forEach((form) => {
      if (form.__pw) return;
      form.__pw = true;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const panel = document.createElement('div');
      panel.className = 'pw-suggest';
      panel.hidden = true;
      panel.setAttribute('role', 'listbox');
      form.appendChild(panel);
      input.setAttribute('aria-autocomplete', 'list');
      let timer;
      let ctrl;
      let active = -1;
      const close = () => {
        panel.hidden = true;
        active = -1;
        form.classList.remove('is-open');
      };
      // phones: cap the results to the space left above the bottom nav / keyboard (it scrolls inside past that)
      const fit = () => {
        if (panel.hidden) return;
        if (!window.matchMedia('(max-width: 749px)').matches) return panel.style.removeProperty('--pw-suggest-max');
        const vv = window.visualViewport;
        let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        const nav = document.querySelector('.pw-bottomnav');
        const nr = nav && getComputedStyle(nav).display !== 'none' && getComputedStyle(nav).visibility !== 'hidden' ? nav.getBoundingClientRect() : null;
        if (nr && nr.height > 0 && nr.top < bottom) bottom = nr.top;
        const room = Math.floor(bottom - 10 - panel.getBoundingClientRect().top);
        panel.style.setProperty('--pw-suggest-max', Math.max(180, room) + 'px');
      };
      window.visualViewport?.addEventListener('resize', fit);
      window.addEventListener('scroll', fit, { passive: true });
      const render = (q, data) => {
        const products = data?.resources?.results?.products || [];
        const colls = (data?.resources?.results?.collections || []).filter((c) => /^set-/.test(c.handle) || true).slice(0, 4);
        if (!products.length && !colls.length) {
          panel.innerHTML = `<div class="pw-suggest__empty">No cards for “${escapeHtml(q)}” right now. <a href="${PWC.routes.allCollection}?view=shop-by-list">Add it to a want list →</a></div>`;
        } else {
          panel.innerHTML =
            (colls.length
              ? `<div class="pw-suggest__sets">${colls
                  .map((c) => `<a class="pw-chip" href="${c.url}">${escapeHtml(c.title)}</a>`)
                  .join('')}</div>`
              : '') +
            products
              .map((p) => {
                const [name, rest] = p.title.split(' - ');
                const img = p.featured_image?.url || p.image;
                return `<a class="pw-suggest__item${p.available ? '' : ' is-out'}" role="option" href="${p.url}">
                  <span class="pw-suggest__img">${img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=96" alt="" loading="lazy">` : ''}</span>
                  <span class="pw-suggest__text"><strong>${escapeHtml(name)}</strong><small>${escapeHtml(rest || '')}</small></span>
                  <span class="pw-suggest__price">${p.available ? PWC.money(Math.round(parseFloat(p.price) * 100)) : 'Sold out'}</span>
                </a>`;
              })
              .join('') +
            `<a class="pw-suggest__all" href="${PWC.routes.search}?q=${encodeURIComponent(q)}&type=product&options%5Bprefix%5D=last">See all results for “${escapeHtml(q)}” →</a>`;
        }
        panel.hidden = false;
        form.classList.add('is-open');
        fit();
      };
      const run = async () => {
        const q = input.value.trim();
        if (q.length < 2) return close();
        ctrl?.abort();
        ctrl = new AbortController();
        try {
          const url = `${PWC.routes.search}/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=7&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.sku,tag`;
          const data = await fetch(url, { signal: ctrl.signal }).then((r) => r.json());
          if (input.value.trim() === q) render(q, data);
        } catch (e) {
          /* aborted */
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(run, 160);
      });
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && panel.innerHTML) {
          panel.hidden = false;
          form.classList.add('is-open');
          fit();
        }
      });
      input.addEventListener('keydown', (e) => {
        const items = $$('.pw-suggest__item, .pw-suggest__all', panel);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (panel.hidden || !items.length) return;
          e.preventDefault();
          active = (active + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items.forEach((it, i) => it.classList.toggle('is-active', i === active));
          items[active].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && active >= 0 && items[active]) {
          e.preventDefault();
          window.location.href = items[active].href;
        } else if (e.key === 'Escape') {
          close();
        }
      });
      document.addEventListener('click', (e) => {
        if (!form.contains(e.target)) close();
      });
    });
  }

  /* "/" focuses the nearest search box (like GitHub / TCGplayer) */
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
    const input = $$('form[data-pw-instant] input[name="q"]').find((i) => i.offsetParent !== null);
    if (input) {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });

  /* Typewriter placeholder on the hero search */
  function initTypewriter() {
    const input = $('[data-pw-typewriter]');
    if (!input || reduceMotion) return;
    const words = (input.dataset.pwTypewriter || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!words.length) return;
    const base = input.placeholder;
    let wi = 0;
    let ci = 0;
    let dir = 1;
    let paused = false;
    input.addEventListener('focus', () => {
      paused = true;
      input.placeholder = base;
    });
    input.addEventListener('blur', () => (paused = false));
    // Calmer typing: steady letters, a proper pause on each full word, then a soft delete
    const step = () => {
      let wait = 230;
      if (!paused && !input.value) {
        const w = words[wi];
        ci += dir;
        input.placeholder = `Try “${w.slice(0, ci)}”`;
        if (dir > 0 && ci >= w.length) {
          dir = -1;
          wait = 3400;
        } else if (dir < 0 && ci <= 1) {
          dir = 1;
          ci = 0;
          wi = (wi + 1) % words.length;
          wait = 900;
        } else {
          wait = dir > 0 ? 230 : 90;
        }
      }
      setTimeout(step, wait);
    };
    setTimeout(step, 1600);
  }

  /* --------------------------------------------------------------- hero fan */
  async function initHero() {
    const fan = $('[data-pw-fan]');
    if (!fan) return;
    if (window.__pwFan) await window.__pwFan; // filled early by the inline script in pwc-hero.liquid
    const ghosts = $$('.pw-fan__card--ghost', fan);
    if (ghosts.length && fan.dataset.source && !window.__pwFan) {
      try {
        const html = await fetch(fan.dataset.source).then((r) => r.text());
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const cards = $$('.pw-card', tmp).slice(0, ghosts.length);
        cards.forEach((c, i) => {
          const img = c.querySelector('img');
          const link = c.querySelector('a');
          const price = c.querySelector('.pw-price')?.textContent?.trim() || '';
          const slot = ghosts[i];
          const a = document.createElement('a');
          a.className = 'pw-fan__card';
          a.href = link?.getAttribute('href') || '#';
          a.tabIndex = -1;
          a.style.setProperty('--i', i);
          const src = img?.currentSrc || img?.getAttribute('src') || '';
          a.innerHTML = `<span class="pw-fan__inner"><img src="${src}" alt="" decoding="async"></span><span class="pw-fan__tag">${escapeHtml(
            price
          )}</span>`;
          slot.replaceWith(a);
        });
      } catch (e) {
        /* keep ghosts */
      }
    }
    fan.classList.add('is-ready');
    // Back button on phones: the page comes back from memory mid-animation, so clear anything half-done
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      $$('.pw-fan__card', fan).forEach((c) => {
        c.style.opacity = '';
        c.style.transition = '';
      });
      if (document.activeElement && fan.contains(document.activeElement)) document.activeElement.blur();
    });
    if (!finePointer || reduceMotion) return;
    const hero = fan.closest('[data-pw-hero]');
    let raf;
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        fan.style.setProperty('--px', x.toFixed(3));
        fan.style.setProperty('--py', y.toFixed(3));
      });
    });
    hero.addEventListener('pointerleave', () => {
      fan.style.setProperty('--px', 0);
      fan.style.setProperty('--py', 0);
    });
  }

  /* -------------------------------------------------------------- set logos */
  const TCGDEX = 'https://api.tcgdex.net/v2/en/sets';
  let setsPromise;
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  PWC.norm = norm;
  function tcgdexSets() {
    if (setsPromise) return setsPromise;
    const cached = store.get('pw-tcgdex-sets', null);
    if (cached && Date.now() - cached.t < 7 * 864e5) {
      setsPromise = Promise.resolve(cached.d);
      return setsPromise;
    }
    setsPromise = fetch(TCGDEX)
      .then((r) => r.json())
      .then((list) => {
        const d = list.map((s) => ({ id: s.id, n: s.name, l: s.logo || '', s: s.symbol || '' }));
        store.set('pw-tcgdex-sets', { t: Date.now(), d });
        return d;
      })
      .catch(() => []);
    return setsPromise;
  }
  const codeFromHandle = (h) => {
    const m = String(h || '').match(/-((?:sv|swsh|me|sm|xy|bw|hgss|dp|pl|ex|base|gym|neo|ecard)\d+(?:-\d+)?[a-z]?|[a-z]{1,4}p|cel25|g1|col1|lc|dc1|det1|si1|rc|mee)$/);
    return m ? m[1].replace(/-(\d)/, '.$1') : '';
  };
  async function initSetLogos(root = document) {
    const els = $$('[data-pw-setlogo]:not([data-pw-done])', root);
    if (!els.length) return;
    const sets = await tcgdexSets();
    if (!sets.length) return;
    const byName = new Map();
    const byId = new Map();
    sets.forEach((s) => {
      if (!byName.has(norm(s.n))) byName.set(norm(s.n), s);
      byId.set(s.id.toLowerCase(), s);
    });
    els.forEach((el) => {
      el.setAttribute('data-pw-done', '');
      // Japanese / Chinese sets: TCGdex's English list would give the wrong logo
      if (/^set-(jp|cn)-/.test(el.dataset.handle || '')) return;
      const code = codeFromHandle(el.dataset.handle);
      const hit = byId.get(code) || byName.get(norm(el.dataset.pwSetlogo));
      const wantSymbol = el.hasAttribute('data-symbol');
      const src = hit && (wantSymbol ? hit.s : hit.l);
      if (!src) return;
      const img = new Image();
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = `${src}.webp`;
      img.onerror = () => {
        if (!img.dataset.retry) {
          img.dataset.retry = '1';
          img.src = `${src}.png`;
        }
      };
      img.onload = () => {
        el.classList.add('has-logo');
        el.hidden = false;
      };
      el.querySelector('.pw-settile__fallback')?.classList.add('is-under');
      el.appendChild(img);
    });
  }

  /* ------------------------------------------------------------ sets browse */
  function initSets() {
    const root = $('[data-pw-sets]');
    if (!root) return;
    const tpl = $('[data-pw-setgrid-tpl]', root);
    if (tpl) {
      // Home page: build the grid only when it's about to be seen, or when someone uses the set search
      let built = false;
      const build = () => {
        if (built) return;
        built = true;
        io && io.disconnect();
        tpl.replaceWith(tpl.content);
        setupSets(root);
        initSetLogos(root);
      };
      const io = 'IntersectionObserver' in window
        ? new IntersectionObserver((en) => en.some((e) => e.isIntersecting) && build(), { rootMargin: '600px 0px' })
        : null;
      if (io) io.observe(root);
      else build();
      $('[data-pw-set-search]', root)?.addEventListener('focus', build, { once: true });
      return;
    }
    setupSets(root);
  }
  function setupSets(root) {
    const grid = $('[data-pw-setgrid]', root);
    const tiles = $$('.pw-settile', grid);
    const eraOrder = $$('.pw-tab', root).map((t) => t.dataset.era);
    // newest first inside each era: sort on the numeric part of the set code
    const key = (h) => {
      const c = codeFromHandle(h);
      const m = c.match(/(\d+)(?:\.(\d+))?([a-z])?$/);
      return m ? Number(m[1]) + (m[2] ? Number(m[2]) / 10 : 0) + (m[3] ? 0.01 : 0) : 0;
    };
    tiles
      .sort((a, b) => {
        const ea = eraOrder.indexOf(a.dataset.era);
        const eb = eraOrder.indexOf(b.dataset.era);
        return ea - eb || key(b.dataset.handle) - key(a.dataset.handle);
      })
      .forEach((t) => grid.appendChild(t));
    const present = new Set(tiles.map((t) => t.dataset.era));
    const tabs = $$('.pw-tab', root).filter((t) => present.has(t.dataset.era));
    tabs.forEach((t) => (t.hidden = false));
    let current = tabs[0]?.dataset.era;
    const search = $('[data-pw-set-search]', root);
    const apply = () => {
      const q = norm(search?.value);
      tiles.forEach((t) => {
        const match = q ? t.dataset.name.includes(q) || norm(t.dataset.name).includes(q) : t.dataset.era === current;
        t.hidden = !match;
      });
      tabs.forEach((t) => t.setAttribute('aria-selected', String(!q && t.dataset.era === current)));
      root.classList.toggle('is-searching', !!q);
    };
    tabs.forEach((t) =>
      t.addEventListener('click', () => {
        current = t.dataset.era;
        if (search) search.value = '';
        apply();
        grid.animate?.([{ opacity: 0.3, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], { duration: 260 });
      })
    );
    search?.addEventListener('input', apply);
    apply();
  }

  /* --------------------------------------------------------- want list (♥) */
  const WKEY = 'pw-wants';
  PWC.wants = {
    all: () => store.get(WKEY, []),
    has: (h) => PWC.wants.all().some((w) => w.h === h),
    toggle(entry) {
      const list = PWC.wants.all();
      const i = list.findIndex((w) => w.h && w.h === entry.h);
      if (i >= 0) list.splice(i, 1);
      else list.unshift({ ...entry, at: Date.now() });
      store.set(WKEY, list);
      PWC.wants.paint();
      return i < 0;
    },
    addText(lines) {
      const list = PWC.wants.all();
      lines.forEach((t) => {
        if (!list.some((w) => w.t === t && !w.h)) list.push({ t, at: Date.now() });
      });
      store.set(WKEY, list);
      PWC.wants.paint();
    },
    remove(pred) {
      store.set(WKEY, PWC.wants.all().filter((w) => !pred(w)));
      PWC.wants.paint();
    },
    paint() {
      const list = PWC.wants.all();
      const handles = new Set(list.map((w) => w.h).filter(Boolean));
      $$('[data-pw-wish]').forEach((btn) => {
        let h;
        try {
          h = JSON.parse(btn.dataset.pwWish).h;
        } catch (e) {
          return;
        }
        const on = handles.has(h);
        btn.setAttribute('aria-pressed', String(on));
        const label = btn.querySelector('[data-pw-wish-label]');
        if (label) label.textContent = on ? 'On your want list' : 'Want list';
      });
      $$('[data-pw-wants-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = !list.length;
      });
    },
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pw-wish]');
    if (!btn) return;
    e.preventDefault();
    let entry;
    try {
      entry = JSON.parse(btn.dataset.pwWish);
    } catch (err) {
      return;
    }
    const card = btn.closest('.pw-card, [data-pw-pdp]');
    entry.t = card?.querySelector('.pw-card__name, .pw-pdp__title')?.textContent?.trim() || entry.h;
    const added = PWC.wants.toggle(entry);
    if (added && !reduceMotion) {
      btn.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 360, easing: 'ease-out' });
      burst(btn);
    }
    PWC.toast(
      `<div class="pw-toast__main"><div class="pw-toast__text"><span class="pw-eyebrow">${added ? 'Saved' : 'Removed'}</span><strong>${escapeHtml(
        entry.t
      )}</strong><span class="pw-muted">${added ? 'Added to your want list.' : 'Taken off your want list.'}</span></div><a class="pw-btn pw-btn--sm pw-btn--ghost" href="${PWC.routes.allCollection}?view=shop-by-list#wants">Open list</a></div>`,
      { timeout: 2600 }
    );
  });

  function burst(el) {
    const r = el.getBoundingClientRect();
    for (let i = 0; i < 8; i++) {
      const s = document.createElement('span');
      s.className = 'pw-burst';
      s.style.left = `${r.left + r.width / 2}px`;
      s.style.top = `${r.top + r.height / 2}px`;
      document.body.appendChild(s);
      const ang = (i / 8) * Math.PI * 2;
      s.animate(
        [
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
          { transform: `translate(calc(-50% + ${Math.cos(ang) * 26}px), calc(-50% + ${Math.sin(ang) * 26}px)) scale(.3)`, opacity: 0 },
        ],
        { duration: 520, easing: 'cubic-bezier(.2,.8,.2,1)' }
      ).finished.then(() => s.remove());
    }
  }

  /* ------------------------------------------------------ recently viewed */
  const RKEY = 'pw-recent';
  function trackRecent() {
    const pdp = $('[data-pw-pdp]');
    if (!pdp) return;
    const h = pdp.dataset.handle;
    const list = store.get(RKEY, []).filter((x) => x !== h);
    list.unshift(h);
    store.set(RKEY, list.slice(0, 16));
  }
  async function renderCardsInto(track, handles) {
    const htmls = await Promise.all(
      handles.map((h) =>
        fetch(`${PWC.routes.root}products/${encodeURIComponent(h)}?view=pwc-card`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    );
    track.innerHTML = htmls.join('');
    $$('.pw-card', track).forEach((c, i) => {
      c.classList.add('pw-pop');
      c.style.setProperty('--d', `${Math.min(i, 8) * 45}ms`);
    });
    hydrate(track);
    return $$('.pw-card', track).length;
  }
  PWC.renderCardsInto = renderCardsInto;
  async function initRecent() {
    const sec = $('[data-pw-recent]');
    if (!sec) return;
    const current = $('[data-pw-pdp]')?.dataset.handle;
    const list = store.get(RKEY, []).filter((h) => h !== current).slice(0, 12);
    if (!list.length) return;
    const n = await renderCardsInto($('[data-pw-recent-track]', sec), list);
    if (n) sec.hidden = false;
    $('[data-pw-recent-clear]', sec)?.addEventListener('click', () => {
      store.set(RKEY, current ? [current] : []);
      sec.hidden = true;
    });
  }

  /* -------------------------------------------------------- product page */
  function initPDP() {
    const pdp = $('[data-pw-pdp]');
    if (!pdp) return;
    const form = $('[data-pw-buy]', pdp);
    const priceEl = $('[data-pw-price]', pdp);
    const stockEl = $('[data-pw-stockline]', pdp);
    const qtyInput = $('[data-pw-qty] input', pdp);
    const atc = $('[data-pw-atc]', pdp);
    const stickyPrice = $('[data-pw-sticky-price]', pdp);

    const clampQty = () => {
      if (!qtyInput) return;
      const max = Number(qtyInput.max || 0);
      let v = Math.max(1, Number(qtyInput.value || 1));
      if (max) v = Math.min(max, v);
      qtyInput.value = v;
    };
    $$('[data-pw-qty-step]', pdp).forEach((b) =>
      b.addEventListener('click', () => {
        qtyInput.value = Number(qtyInput.value || 1) + Number(b.dataset.pwQtyStep);
        clampQty();
      })
    );
    qtyInput?.addEventListener('change', clampQty);

    form?.addEventListener('change', (e) => {
      const r = e.target.closest('input[type="radio"][name="id"]');
      if (!r) return;
      priceEl.textContent = r.dataset.price;
      if (stickyPrice) stickyPrice.textContent = r.dataset.price;
      const q = r.dataset.qty;
      if (qtyInput) {
        if (q) qtyInput.max = q;
        else qtyInput.removeAttribute('max');
        clampQty();
      }
      const n = Number(q || 0);
      stockEl.innerHTML =
        r.dataset.available === 'true'
          ? n && n <= 3
            ? `<span class="pw-stock pw-stock--low">${n === 1 ? 'Last one in stock' : `Only ${n} left`}</span>`
            : '<span class="pw-stock pw-stock--ok">In stock</span>'
          : '<span class="pw-stock pw-stock--out">Sold out</span>';
      if (atc) atc.disabled = r.dataset.available !== 'true';
      try {
        history.replaceState(history.state, '', r.dataset.url);
      } catch (err) {
        /* ignore */
      }
      priceEl.animate?.([{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }], { duration: 240 });
    });

    // Flip front/back
    const flip = $('[data-pw-flip]', pdp);
    const flipBtn = $('[data-pw-flip-btn]', pdp);
    flipBtn?.addEventListener('click', () => {
      const on = flip.classList.toggle('is-flipped');
      flipBtn.setAttribute('aria-pressed', String(on));
    });
    $$('[data-pw-thumb]', pdp).forEach((t) =>
      t.addEventListener('click', () => {
        $$('[data-pw-thumb]', pdp).forEach((x) => x.classList.toggle('is-on', x === t));
        const idx = Number(t.dataset.pwThumb);
        if (flip && idx <= 1) {
          flip.classList.toggle('is-flipped', idx === 1);
          flipBtn?.setAttribute('aria-pressed', String(idx === 1));
        } else {
          const front = $('.pw-flip__front img', pdp);
          if (front) {
            front.srcset = '';
            front.src = t.dataset.src;
          }
          flip?.classList.remove('is-flipped');
        }
        const zi = $('[data-pw-zoom-pane] img', pdp);
        if (zi) zi.src = t.dataset.src;
      })
    );

    // Zoom dialog with pan
    const dlg = $('[data-pw-zoom]', pdp);
    const pane = $('[data-pw-zoom-pane]', pdp);
    const zimg = $('[data-pw-zoom-pane] img', pdp);
    const openZoom = () => {
      if (!dlg) return;
      if (flip?.classList.contains('is-flipped')) {
        const back = $('.pw-flip__back img', pdp);
        if (back) zimg.src = back.currentSrc || back.src;
      }
      dlg.showModal();
      dlg.classList.remove('is-zoomed');
    };
    $('[data-pw-zoom-btn]', pdp)?.addEventListener('click', openZoom);
    $('.pw-stage__tilt', pdp)?.addEventListener('dblclick', openZoom);
    $('[data-pw-zoom-close]', pdp)?.addEventListener('click', () => dlg.close());
    dlg?.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close();
    });
    pane?.addEventListener('click', () => dlg.classList.toggle('is-zoomed'));
    pane?.addEventListener('pointermove', (e) => {
      const r = pane.getBoundingClientRect();
      zimg.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
      zimg.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
    });

    // Sticky buy bar once the main button scrolls away
    const sticky = $('[data-pw-stickybuy]', pdp);
    if (sticky && form && 'IntersectionObserver' in window) {
      // Show once the main Add to cart button has scrolled away, hide again over the footer
      let pastForm = false;
      let atFooter = false;
      const update = () => {
        const show = pastForm && !atFooter;
        sticky.classList.toggle('is-on', show);
        sticky.setAttribute('aria-hidden', String(!show));
      };
      new IntersectionObserver(([en]) => {
        pastForm = !en.isIntersecting && en.boundingClientRect.top < 0;
        update();
      }).observe(form);
      const footer = document.querySelector('.pw-footer, footer');
      if (footer)
        new IntersectionObserver(([en]) => {
          atFooter = en.isIntersecting;
          update();
        }).observe(footer);
    }

    initVersions();
  }

  /* "Other <Pokémon> cards" rail on the product page */
  async function initVersions() {
    const sec = $('[data-pw-versions]');
    if (!sec) return;
    const name = sec.dataset.pwVersions;
    const exclude = sec.dataset.exclude;
    // drop stamps and variants in brackets, e.g. "Pikachu (CoroCoro)" or "Mewtwo ex [Staff]", then the mechanic suffixes
    const plain = name.replace(/\s*[([][^)\]]*[)\]]/g, '').replace(/\s{2,}/g, ' ').trim() || name;
    const base = plain.replace(/\b(ex|gx|v|vmax|vstar|break|lv\.?x|prime|legend|star|δ)\b/gi, '').replace(/\s{2,}/g, ' ').trim() || plain;
    try {
      const data = await fetch(
        `${PWC.routes.search}?q=${encodeURIComponent(base)}&type=product&options%5Bprefix%5D=last&view=pwc-data`
      ).then((r) => r.json());
      const list = (data.products || []).filter((p) => String(p.id) !== String(exclude) && p.available);
      const nb = norm(base);
      const same = list.filter((p) => norm(p.title.split(' - ')[0]).includes(nb)).slice(0, 12);
      if (!same.length) return;
      await renderCardsInto($('[data-pw-versions-track]', sec), same.map((p) => p.handle));
      const h2 = $('.pw-h2', sec);
      if (h2) h2.textContent = `More ${base} cards`;
      const all = $('.pw-sec-head__actions a', sec);
      if (all) all.href = `${PWC.routes.search}?q=${encodeURIComponent(base)}&type=product&options%5Bprefix%5D=last`;
      sec.hidden = false;
    } catch (e) {
      /* no rail */
    }
  }

  /* ---------------------------------------------------------------- catalog */
  function initCatalog() {
    const cat = $('[data-pw-catalog]');
    if (!cat) return;
    const sectionId = cat.dataset.sectionId;

    const setBusy = (on) => cat.classList.toggle('is-loading', on);
    const swap = async (url, { push = true } = {}) => {
      setBusy(true);
      try {
        const u = new URL(url, location.origin);
        u.searchParams.set('section_id', sectionId);
        const html = await fetch(u.toString()).then((r) => r.text());
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const next = $('[data-pw-catalog]', tmp);
        if (!next) throw new Error('no section');
        const keepScroll = window.scrollY;
        cat.innerHTML = next.innerHTML;
        cat.dataset.view = next.dataset.view;
        u.searchParams.delete('section_id');
        if (push) history.pushState({ pw: true }, '', u.pathname + u.search);
        bindCatalog();
        hydrate(cat);
        const top = $('[data-pw-toolbar]', cat)?.getBoundingClientRect().top;
        if (top !== undefined && top < 0) window.scrollTo({ top: keepScroll + top - 90, behavior: 'smooth' });
      } catch (e) {
        window.location.href = url;
      } finally {
        setBusy(false);
      }
    };
    window.addEventListener('popstate', () => swap(location.href, { push: false }));

    const formUrl = (form) => {
      const u = new URL(form.action, location.origin);
      const fd = new FormData(form);
      for (const [k, v] of fd.entries()) if (v !== '') u.searchParams.append(k, v);
      return u.toString();
    };

    let io;
    function bindCatalog() {
      const form = $('[data-pw-filter-form]', cat);
      const drawer = $('[data-pw-filters]', cat);
      form?.addEventListener('change', (e) => {
        if (e.target.matches('[data-pw-fsearch]')) return;
        if (e.target.type === 'number') return;
        swap(formUrl(form));
      });
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        swap(formUrl(form));
      });
      $$('[data-pw-fsearch]', cat).forEach((inp) =>
        inp.addEventListener('input', () => {
          const q = inp.value.trim().toLowerCase();
          $$('li[data-label]', inp.parentElement).forEach((li) => (li.hidden = q && !li.dataset.label.includes(q)));
        })
      );
      $$('a[data-pw-nav]', cat).forEach((a) =>
        a.addEventListener('click', (e) => {
          if (e.metaKey || e.ctrlKey) return;
          e.preventDefault();
          swap(a.href);
        })
      );
      $('[data-pw-sort]', cat)?.addEventListener('change', (e) => {
        const u = new URL(location.href);
        u.searchParams.set('sort_by', e.target.value);
        u.searchParams.delete('page');
        swap(u.toString());
      });
      $$('[data-pw-view]', cat).forEach((a) =>
        a.addEventListener('click', (e) => {
          e.preventDefault();
          const u = new URL(location.href);
          u.searchParams.delete('page');
          if (a.dataset.pwView === 'list') u.searchParams.set('view', 'list');
          else u.searchParams.delete('view');
          store.set('pw-view', a.dataset.pwView);
          window.location.href = u.toString(); // different template → full load
        })
      );
      $$('[data-pw-filters-open]', cat).forEach((b) =>
        b.addEventListener('click', () => {
          drawer.classList.add('is-open');
          document.documentElement.classList.add('pw-lock');
        })
      );
      $$('[data-pw-filters-close]', cat).forEach((b) =>
        b.addEventListener('click', () => {
          drawer.classList.remove('is-open');
          document.documentElement.classList.remove('pw-lock');
        })
      );

      // Load more (+ infinite scroll after the first click)
      const more = $('[data-pw-loadmore]', cat);
      io?.disconnect();
      if (more) {
        let loading = false;
        const load = async () => {
          if (loading) return;
          loading = true;
          more.classList.add('is-busy');
          try {
            const u = new URL(more.href, location.origin);
            u.searchParams.set('section_id', sectionId);
            let resp = await fetch(u.toString());
            if (resp.status === 429) {
              await new Promise((r) => setTimeout(r, 1200));
              resp = await fetch(u.toString());
            }
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const html = await resp.text();
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const items = $('[data-pw-items]', tmp);
            const target = $('[data-pw-items]', cat);
            const added = Array.from(items?.children || []);
            added.forEach((n, i) => {
              n.classList.add('pw-pop');
              n.style.setProperty('--d', `${Math.min(i, 10) * 30}ms`);
              target.appendChild(n);
            });
            hydrate(target);
            const nextMore = $('[data-pw-loadmore]', tmp);
            const shown = $('[data-pw-shown]', cat);
            if (shown) {
              const nShown = Number(shown.textContent) + added.length;
              shown.textContent = nShown;
              const total = Number($('.pw-toolbar__count strong', cat)?.textContent || nShown);
              $('[data-pw-bar]', cat).style.width = `${Math.min(100, (nShown / total) * 100)}%`;
            }
            // Search: a page with no exact matches means the rest are only "similar" cards. Stop
            // scrolling for more and offer the "Show similar cards" button instead.
            const strictMore = $('[data-pw-strict]', cat);
            if (strictMore && !cat.classList.contains('pw-fuzzy-on')) {
              const real = added.filter((n) => !n.classList.contains('pw-fuzzy')).length;
              const fuzzy = added.length - real;
              if (fuzzy && !$('[data-pw-fuzzy-toggle]', cat)) {
                const b = document.createElement('button');
                b.type = 'button';
                b.className = 'pw-btn pw-btn--ghost pw-btn--wide';
                b.setAttribute('data-pw-fuzzy-toggle', '');
                b.textContent = 'Show similar cards too';
                strictMore.prepend(b);
              }
              if (!real) {
                io?.disconnect();
                io = null;
                more.hidden = true;
              }
            }
            const pages = $('.pw-pages', cat);
            if (pages) pages.remove();
            if (nextMore) {
              more.href = nextMore.href;
              if (!io && 'IntersectionObserver' in window) {
                io = new IntersectionObserver(([en]) => en.isIntersecting && load(), { rootMargin: '900px 0px' });
                io.observe(more);
              }
            } else {
              io?.disconnect();
              more.remove();
            }
          } catch (e) {
            window.location.href = more.href;
          } finally {
            loading = false;
            more.classList.remove('is-busy');
          }
        };
        more.addEventListener('click', (e) => {
          e.preventDefault();
          load();
        });
      }
      cat.addEventListener('click', (e) => {
        const t = e.target.closest('[data-pw-fuzzy-toggle]');
        if (!t) return;
        cat.classList.add('pw-fuzzy-on');
        t.remove();
        if (more) more.hidden = false;
      });
    }
    bindCatalog();

    // Row hover preview (list view)
    if (finePointer) {
      let peek;
      cat.addEventListener('pointerover', (e) => {
        const a = e.target.closest('[data-pw-peek]');
        if (!a) return;
        peek = peek || Object.assign(document.createElement('img'), { className: 'pw-peek', alt: '' });
        peek.src = a.dataset.pwPeek;
        document.body.appendChild(peek);
        const r = a.getBoundingClientRect();
        peek.style.left = `${r.right + 14}px`;
        peek.style.top = `${Math.max(12, Math.min(window.innerHeight - 340, r.top - 120))}px`;
        peek.classList.add('is-on');
      });
      cat.addEventListener('pointerout', (e) => {
        if (e.target.closest('[data-pw-peek]')) peek?.classList.remove('is-on');
      });
    }
  }

  /* ---------------------------------------------------------- set checklist */
  async function initChecklist() {
    const root = $('[data-pw-checklist]');
    if (!root) return;
    const grid = $('[data-pw-cl-grid]', root);
    const sub = $('[data-pw-cl-sub]', root);
    let stock = [];
    try {
      stock = JSON.parse($('[data-pw-cl-stockdata]', root).textContent);
    } catch (e) {
      stock = [];
    }
    const nk = (n) =>
      String(n || '')
        .split('/')[0]
        .trim()
        .toLowerCase()
        .replace(/^([a-z]*)0+(\d)/, '$1$2');
    const byNum = new Map();
    stock
      .filter((s) => s.available)
      .forEach((s) => {
        const k = nk(s.number || s.title.split(' ').pop());
        const cur = byNum.get(k);
        if (!cur || s.price < cur.price) byNum.set(k, s);
      });

    const sets = await tcgdexSets();
    const code = codeFromHandle(root.dataset.handle);
    const meta = sets.find((s) => s.id.toLowerCase() === code) || sets.find((s) => norm(s.n) === norm(root.dataset.name));
    let cards = [];
    if (meta) {
      try {
        const d = await fetch(`${TCGDEX}/${encodeURIComponent(meta.id)}`).then((r) => r.json());
        cards = (d.cards || []).map((c) => ({ id: c.localId, name: c.name, img: c.image ? `${c.image}/low.webp` : '' }));
      } catch (e) {
        cards = [];
      }
    }
    if (!cards.length) {
      // Fallback: only what we stock
      cards = stock.map((s) => ({ id: s.number?.split('/')[0] || '', name: s.title.split(' - ')[0], img: s.image }));
    }
    const KEY = `pw-owned-${meta?.id || root.dataset.handle}`;
    let owned = new Set(store.get(KEY, []));
    let filter = 'all';

    const render = () => {
      const rows = cards.map((c) => ({ ...c, s: byNum.get(nk(c.id)), own: owned.has(c.id) }));
      const vis = rows.filter((r) =>
        filter === 'stock' ? r.s : filter === 'need' ? !r.own : filter === 'owned' ? r.own : true
      );
      grid.innerHTML = vis
        .map(
          (r) => `<div class="pw-ctile${r.s ? ' is-stock' : ''}${r.own ? ' is-owned' : ''}">
            <a class="pw-ctile__img" ${r.s ? `href="${r.s.url}"` : ''}>
              ${r.img ? `<img src="${r.img}" alt="" loading="lazy" decoding="async">` : ''}
              ${r.s && !r.own ? '<span class="pw-ctile__badge">In stock</span>' : ''}
            </a>
            <button type="button" class="pw-ctile__own" data-own="${escapeHtml(r.id)}" aria-pressed="${r.own}" aria-label="Mark ${escapeHtml(r.name)} as owned">✓</button>
            <div class="pw-ctile__info">
              <span class="pw-ctile__num">#${escapeHtml(r.id)}</span>
              <strong>${escapeHtml(r.name)}</strong>
              <div class="pw-ctile__foot">
                ${
                  r.s
                    ? `<span class="pw-price">${PWC.money(r.s.price)}</span>${
                        r.s.multi > 1
                          ? `<a class="pw-add" href="${r.s.url}" aria-label="Choose a copy"><svg viewBox="0 0 20 20"><path d="M4 10h12M11 5l5 5-5 5"/></svg></a>`
                          : `<form class="pw-add-form" data-pw-add><input type="hidden" name="id" value="${r.s.variant}"><input type="hidden" name="quantity" value="1"><button class="pw-add" type="submit" aria-label="Add ${escapeHtml(
                              r.name
                            )}"><svg class="pw-add__plus" viewBox="0 0 20 20"><path d="M10 4v12M4 10h12"/></svg><svg class="pw-add__tick" viewBox="0 0 20 20"><path d="M4 10.5l4 4 8-9"/></svg></button></form>`
                      }`
                    : `<span class="pw-muted">Not in stock</span>`
                }
              </div>
            </div>
          </div>`
        )
        .join('');
      const inStock = rows.filter((r) => r.s).length;
      const ownN = rows.filter((r) => r.own).length;
      const pct = rows.length ? Math.round((ownN / rows.length) * 100) : 0;
      $('[data-pw-cl-ring]', root).style.setProperty('--p', pct);
      $('[data-pw-cl-pct]', root).textContent = `${pct}%`;
      $('[data-pw-cl-owned]', root).textContent = `${ownN} of ${rows.length} owned`;
      $('[data-pw-cl-stock]', root).textContent = `${inStock} in stock at PW`;
      sub.textContent = meta ? `${rows.length} cards in the set` : 'Showing the cards we stock';
      const missing = rows.filter((r) => r.s && !r.own && r.s.multi === 1);
      const btn = $('[data-pw-cl-addmissing]', root);
      btn.disabled = !missing.length;
      btn.textContent = missing.length
        ? `Add ${missing.length} missing · ${PWC.money(missing.reduce((n, r) => n + r.s.price, 0))}`
        : 'Nothing missing in stock';
      btn.onclick = async () => {
        btn.classList.add('is-busy');
        try {
          await PWC.addItems(missing.map((r) => ({ id: r.s.variant, quantity: 1 })), { sourceId: 'pw-checklist' });
        } catch (e) {
          PWC.toast(`<div class="pw-toast__main"><div class="pw-toast__text"><strong>${escapeHtml(e.message)}</strong></div></div>`);
        } finally {
          btn.classList.remove('is-busy');
        }
      };
    };
    grid.addEventListener('click', (e) => {
      const b = e.target.closest('[data-own]');
      if (!b) return;
      const id = b.dataset.own;
      if (owned.has(id)) owned.delete(id);
      else owned.add(id);
      store.set(KEY, [...owned]);
      render();
    });
    $$('[data-pw-cl-filter]', root).forEach((t) =>
      t.addEventListener('click', () => {
        filter = t.dataset.pwClFilter;
        $$('[data-pw-cl-filter]', root).forEach((x) => x.setAttribute('aria-selected', String(x === t)));
        render();
      })
    );
    render();
  }

  /* ------------------------------------------------------ bottom navigation */
  function initBottomNav() {
    const nav = $('[data-pw-bottomnav]');
    if (!nav) return;
    $('[data-pw-bottom-search]', nav)?.addEventListener('click', (e) => {
      const input = $$('form[data-pw-instant] input[name="q"]').find((i) => i.offsetParent !== null);
      if (input) {
        e.preventDefault();
        input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        setTimeout(() => input.focus(), 250);
      }
    });
    $('[data-pw-bottom-cart]', nav)?.addEventListener('click', (e) => {
      if (document.querySelector('cart-drawer-component')) {
        e.preventDefault();
        PWC.openCart();
      }
    });
    // Hide after a proper scroll down, show again after a proper scroll up. Small finger
    // movements, momentum tails and the iOS bounce at the top/bottom are ignored so it doesn't flicker.
    let anchorY = Math.max(0, window.scrollY);
    let hidden = false;
    let ticking = false;
    const setHidden = (v) => {
      if (v === hidden) return;
      hidden = v;
      nav.classList.toggle('is-hidden', v);
    };
    const update = () => {
      ticking = false;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const y = Math.min(Math.max(0, window.scrollY), Math.max(0, max));
      if (y < 120) {
        setHidden(false);
        anchorY = y;
        return;
      }
      if (max - y < 4) return; // bouncing at the bottom: leave it as it is
      const d = y - anchorY;
      if (hidden ? d > 0 : d < 0) anchorY = y; // keep the anchor at the turning point
      else if (Math.abs(d) > (hidden ? 40 : 24)) {
        setHidden(!hidden);
        anchorY = y;
      }
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    // Coming back to the page (back button) should always show the menu
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      setHidden(false);
      anchorY = Math.max(0, window.scrollY);
    });
  }

  /* Header "More" dropdown (test theme): close on outside click, Escape, or after picking a link */
  document.addEventListener('click', (e) => {
    $$('[data-pw-navmore][open]').forEach((d) => {
      if (!d.contains(e.target) || e.target.closest('a')) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('[data-pw-navmore][open]').forEach((d) => d.removeAttribute('open'));
  });

  /* Keep the meter in Horizon's drawer fresh whenever the cart changes elsewhere */
  document.addEventListener('cart:update', (e) => {
    const cart = e.detail?.resource;
    if (cart && typeof cart.total_price === 'number') {
      PWC.cartTotal = cart.total_price;
      PWC.renderShipMeters();
    } else if (e.detail?.data?.source !== 'pwc') {
      PWC.getCart()
        .then((c) => {
          PWC.cartTotal = c.total_price;
          PWC.cartCount = c.item_count;
          $$('[data-pw-cart-count]').forEach((el) => {
            el.textContent = c.item_count;
            el.hidden = c.item_count === 0;
          });
          PWC.renderShipMeters();
        })
        .catch(() => {});
    }
  });
  document.addEventListener(
    'click',
    (e) => {
      if (e.target.closest('cart-icon, .header-actions__cart-icon, [data-pw-open-cart]')) setTimeout(PWC.renderShipMeters, 80);
    },
    true
  );

  /* --------------------------------------------------------------- hydrate */
  function hydrate(root = document) {
    initTilt(root);
    initReveal(root);
    PWC.wants.paint();
    initSetLogos(root);
  }
  PWC.hydrate = hydrate;
  PWC.loadRail = loadRail;

  function initCartPage() {
    if (PWC.template !== 'cart' || !PWC.freeShipping) return;
    const main = document.querySelector('#MainContent .shopify-section');
    if (!main || document.querySelector('.pw-cartpage-meter')) return;
    const wrap = document.createElement('div');
    wrap.className = 'pw-scope pw-wrap pw-cartpage-meter';
    wrap.innerHTML = '<div data-pw-shipmeter></div>';
    main.prepend(wrap);
  }

  function boot() {
    document.documentElement.classList.add('pw-js');
    initCartPage();
    trackRecent();
    hydrate();
    initCounts();
    initRails();
    initInstant();
    initTypewriter();
    initHero();
    initSets();
    initCatalog();
    initPDP();
    initRecent();
    initChecklist();
    initBottomNav();
    PWC.renderShipMeters();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
